import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, TextInput } from '@rbilabs/components-library';
import { VisuallyHidden } from '@rbilabs/components-library/build/components/visually-hidden';
import { Field, Formik } from 'formik';
import { debounce, isEqual } from 'lodash';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import { ILocation } from '@rbi-ctg/frontend';
import { AutocompleteAddressInput } from 'components/autocomplete-address-input';
import { FormikTextInput } from 'components/formik/text-input';
import { GeolocationDeniedModal } from 'components/geolocation-permission-modal/geolocation-denied-modal';
import { LocationDetails } from 'components/location-details';
import { Picture } from 'components/picture';
import { IDeliveryAddress } from 'generated/rbi-graphql';
import { AddressFormatType, useAddressFormat } from 'hooks/use-address-format';
import { useDistanceWarning } from 'hooks/use-distance-warning';
import { useFeatureDisclaimers } from 'hooks/use-feature-disclaimers';
import { useCdpContext } from 'state/cdp';
import { ClickEventComponentNames, CustomEventNames, EventTypes } from 'state/cdp/constants';
import { useGeolocation } from 'state/geolocation';
import { useFlag } from 'state/launchdarkly';
import { FormValidationState } from 'utils';
import {
  IPlaceAddress,
  IPlaceData,
  geocodeAddress,
  getCoordinateDetails,
  parseLocationPrediction,
} from 'utils/geolocation';
import { parseGeocodeResult } from 'utils/geolocation/parse-geocode-result';
import { convertCountryStringToIsoCountryCode2, getAppCountry } from 'utils/i18n/country';
import { LaunchDarklyFlag } from 'utils/launchdarkly';

import { StoreLocatorSteps } from '../../../pages/store-locator/hooks/use-store-locator-view';
import { MAP_TAB_INDEX, useDeliveryAddressModalContext } from '../delivery-address-modal.context';
import { DeliveryAddresses } from '../delivery-addresses';
import { MapDeliveryAddress } from '../map-delivery-address/index';
import { ModalContent } from '../styled';
import { IDeliveryAddressFormProps, IPhoneAddressValidation } from '../types';

import { DeliveryAddressFormFooter } from './delivery-address-form-footer';
import { DeliveryAddressFormHeader } from './delivery-address-form-header';
import { DeliveryAddressFormInput } from './delivery-address-form-input';
import {
  AdditionalInformation,
  AddressDetailsBorder,
  AddressDetailsSection,
  AddressNameLabel,
  AddressWrapper,
  ChangeButton,
  DeliveryLogoContainer,
  FieldWrapper,
  HelperText,
  RightArrow,
  StyledForm,
} from './styled';
import { UnrecognizedStreetNumberModal } from './unrecognized-street-number-modal';
import { useDeliveryAddresses } from './use-delivery-addresses';
import { formatAddressLine1, getAddressSubtitle, getSplitAddressTitle } from './utils';

export const buildAddressFromObject = (address: IPlaceAddress): string => {
  return [address?.addressLine1, address?.city, address?.zip].filter(c => !!c).join(', ');
};

export const DeliveryAddressForm: React.FC<IDeliveryAddressFormProps> = ({
  instructions,
  user,
  getDetailsPlaceData,
  showDeliveryAddresses,
  setAddress,
  currentStep,
  setCurrentStep,
  isLoading: loading,
  editedAddress,
  setEditedAddress,
  skipSavedAddressClosedStore,
}) => {
  // #region Flags
  const enablePersistAddressOnStoreSelector = useFlag(
    LaunchDarklyFlag.ENABLE_PERSIST_ADDRESS_ON_STORE_SELECTOR
  );
  const enableSavedAddressesImprovements = useFlag(
    LaunchDarklyFlag.ENABLE_SAVED_ADDRESSES_IMPROVEMENTS
  );
  const enableSaveDeliveryAddress = useFlag(LaunchDarklyFlag.ENABLE_SAVE_DELIVERY_ADDRESS);
  const enableDeliveryShowOnlyStreetNameAndNumber = useFlag(
    LaunchDarklyFlag.ENABLE_DELIVERY_SHOW_ONLY_STREET_NAME_AND_NUMBER
  );
  const enableDeliverySplitAddressDetails = useFlag(
    LaunchDarklyFlag.ENABLE_DELIVERY_SPLIT_ADDRESS_DETAILS
  );
  const enableRecentAddresses = useFlag(LaunchDarklyFlag.ENABLE_RECENT_ADDRESSES);
  const enableReduceDeliveryError = useFlag(LaunchDarklyFlag.ENABLE_REDUCE_DELIVERY_ERRORS);
  const enableMapFallBack = useFlag(LaunchDarklyFlag.ENABLE_MAP_FALLBACK);
  const enableDeliveryOnSameScreen = useFlag(
    LaunchDarklyFlag.ENABLE_DELIVERY_ADDRESS_ON_SAME_SCREEN
  );
  const enableDeliveryGeolocation = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY_GEOLOCATION_2);
  const enableRequireAddress2 = useFlag(
    LaunchDarklyFlag.ENABLE_DELIVERY_DETAILS_FORM_REQUIRE_ADDRESS_2
  );
  // #endregion

  // #region Global state
  const { formatMessage } = useIntl();
  const { featureDisclaimers } = useFeatureDisclaimers();
  const { deliveryLogo } = featureDisclaimers || {};
  const { logNavigationClick, trackEvent } = useCdpContext();
  const {
    isPermissionPrompt,
    isPermissionDenied,
    setPermissionChecking,
    loadCurrentPosition,
    manualAddress,
    manualPlaceId,
    updateManualCoordinates,
    userCoordinates,
  } = useGeolocation();
  const location = useLocation();
  const addressFormat = useAddressFormat();
  const {
    isLocationConfirmationNeeded,
    ConfirmLocationModal,
    dismissDialog,
    openConfirmationModal,
    isLoading: isLoadingGetUserOrders,
    isGeolocationEnabled,
  } = useDistanceWarning();
  const { deliveryAddress, showMapFallback, currentMapFallbackTab, setDeliveryAddressMapFallback } =
    useDeliveryAddressModalContext();
  const { recentAddresses, deliveryAddresses } = useDeliveryAddresses({
    skip: !enableRecentAddresses,
  });
  // #endregion

  // #region Local state
  const deliveryAddressInputRef = useRef<HTMLInputElement>(null);
  const [deliveryAddressFormInputMessage, setDeliveryAddressFormInputMessage] = useState('');
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [validation, setValidation] = useState<IPhoneAddressValidation>({
    phoneNumber: FormValidationState.PRISTINE,
    address: FormValidationState.PRISTINE,
  });
  const [addressString, setAddressString] = useState(
    enablePersistAddressOnStoreSelector ? (manualAddress ?? '') : ''
  );
  const [addressComponents, setAddressComponents] = useState<IPlaceAddress | null>(null);
  const [coordinates, setCoordinates] = useState<ILocation | undefined>();
  const [deliveryInstructions, setDeliveryInstructions] = useState(instructions || '');
  const [geolocationDeniedModalOpen, setGeolocationDeniedModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autocompleteStreetName, setAutocompleteStreetName] = useState('');
  const [autocompletePreviousValue, setAutocompletePreviousValue] = useState('');
  const [autocompleteStreetNameError, setAutocompleteStreetNameError] = useState('');
  const [streetAndNumber, setStreetAndNumber] = useState('');
  const [previousStep, setPreviousStep] = useState<StoreLocatorSteps | null>(null);
  const [addressUnrecognized, setAddressUnrecognized] = useState(false);
  const [showAddressUnrecognizedModal, setShowAddressUnrecognizedModal] = useState(false);
  const [streetNumberError, setStreetNumberError] = useState('');
  // #endregion

  // #region Content display
  const isLoading = loading || isLoadingGetUserOrders;
  const addressTitle = enableDeliverySplitAddressDetails
    ? getSplitAddressTitle(addressComponents, addressFormat === AddressFormatType.NameNumber)
    : streetAndNumber;
  const phoneNumber = user?.details?.phoneNumber || '';
  const showRecentAddresses = enableRecentAddresses && Boolean(recentAddresses.length);
  const showFooter = useMemo(
    () => !showMapFallback || !enableMapFallBack || currentMapFallbackTab === MAP_TAB_INDEX,
    [currentMapFallbackTab, enableMapFallBack, showMapFallback]
  );
  const shouldShowMap = useMemo(
    () => !!(coordinates?.lat && coordinates?.lng) && !showMapFallback,
    [coordinates, showMapFallback]
  );
  const showConfirmManualAddressButton =
    enableDeliveryGeolocation &&
    Boolean(manualAddress) &&
    Boolean(manualPlaceId) &&
    previousStep !== StoreLocatorSteps.CONFIRM_ADDRESS;
  // #endregion

  // #region Form submission handlers
  const confirmDelivery = () => {
    if (addressComponents) {
      setAddress({
        address: addressComponents,
        deliveryInstructions,
        phoneNumber,
        coordinates,
      });

      setDeliveryAddressMapFallback({
        coordinates: {
          lat: 0,
          lng: 0,
        },
        address: {
          addressLine1: '',
          city: '',
          country: getAppCountry(),
        },
      });
    }
  };

  const validateCustomerFaraway = async () => {
    const { lat, lng } = coordinates ?? {};

    if (!lat || !lng) {
      confirmDelivery();
      return;
    }

    const isFarAway = await isLocationConfirmationNeeded({ lat, lng });
    if (isFarAway) {
      openConfirmationModal();
      return;
    }
    confirmDelivery();
  };

  // Validate address confirmation form when enableReduceDeliveryError is enabled
  const isAddressConfirmationFormInvalid = () => {
    if (!enableReduceDeliveryError) {
      return false;
    }

    if (!autocompleteStreetName) {
      const streetNameErrorMessage = formatMessage({ id: 'streetNameRequired' });

      setAutocompleteStreetNameError(streetNameErrorMessage);
      trackEvent({
        name: CustomEventNames.ERROR,
        type: EventTypes.Other,
        attributes: {
          Name: CustomEventNames.STREET_NAME_IS_REQUIRED,
          'Response Title': streetNameErrorMessage,
        },
      });
    }

    if (!addressComponents?.streetNumber) {
      const streetNumberErrorMessage = formatMessage({ id: 'streetNumberRequired' });

      setStreetNumberError(streetNumberErrorMessage);
      trackEvent({
        name: CustomEventNames.ERROR,
        type: EventTypes.Other,
        attributes: {
          Name: CustomEventNames.STREET_NUMBER_IS_REQUIRED,
          'Response Title': streetNumberErrorMessage,
        },
      });
    }

    return !autocompleteStreetName || !addressComponents?.streetNumber;
  };

  // Submit delivery address form
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setDeliveryAddressFormInputMessage('');

    if (validation.address === FormValidationState.PRISTINE && !coordinates) {
      return;
    }

    if (isAddressConfirmationFormInvalid()) {
      return;
    }

    setIsSubmitting(true);

    if (!addressComponents || validation.address === FormValidationState.INVALID) {
      setDeliveryAddressFormInputMessage(formatMessage({ id: 'addressError' }));
      logNavigationClick(CustomEventNames.BUTTON_CLICK_DELIVER_HERE, {
        error: 'Invalid Address',
      });

      if (deliveryAddressInputRef.current) {
        deliveryAddressInputRef.current.focus();
      }
      setIsSubmitting(false);
      return;
    }

    // track attempt to deliver
    logNavigationClick(CustomEventNames.BUTTON_CLICK_DELIVER_HERE);

    if (addressUnrecognized) {
      setShowAddressUnrecognizedModal(true);
      return;
    }

    await validateCustomerFaraway();
  };
  // #endregion

  // #region Helpers
  const isDeliveryHereButtonDisabled = (): boolean => {
    if (enableReduceDeliveryError) {
      return false;
    }

    if (enableDeliverySplitAddressDetails) {
      return !addressComponents?.route?.trim() || !addressComponents.streetNumber?.trim();
    }

    return !addressComponents?.addressLine1 || !!deliveryAddressFormInputMessage;
  };

  // Change current step and automatically set the previous step
  const handleStepChange = (step: StoreLocatorSteps) => {
    setPreviousStep(currentStep);
    setCurrentStep(step);
  };

  // Match current step
  const matchCurrentStep = (step: StoreLocatorSteps) => currentStep === step;

  // Handle invalid address
  const handleInvalidAddress = () => {
    setValidation({ ...validation, address: FormValidationState.INVALID });
    setDeliveryAddressFormInputMessage(formatMessage({ id: 'addressError' }));
  };

  // useMemo is necessary for the debounce to work, otherwise it will be recreated every rerender
  const updateLocationViaGeocoding = useMemo(
    // Adding short debounce to prevent unnecessary calls and decrease cost
    () =>
      debounce(async (address: IPlaceAddress) => {
        const parsedAddress = parseLocationPrediction(address);
        const geocodeResult = await geocodeAddress(parsedAddress);

        if (!geocodeResult) {
          setAddressUnrecognized(true);
          return;
        }

        const placeData = parseGeocodeResult(geocodeResult, {
          useISOCountry: true,
          streetNameFirst: addressFormat === AddressFormatType.NameNumber,
        });

        if (!placeData.address.streetNumber) {
          setAddressUnrecognized(true);
          return;
        }

        setAddressUnrecognized(false);

        handlePlaceChange(geocodeResult.place_id, placeData);
      }, 400),
    [addressFormat]
  );

  // Update address components, when null is passed, the address components are reset
  const updateAddressComponents = (
    payload: Partial<IPlaceAddress> | null,
    geocode: boolean = false
  ) => {
    if (payload == null) {
      setAddressComponents(null);
      return;
    }

    const newAddressComponents = {
      ...(addressComponents ?? {}),
      ...payload,
    } as IPlaceAddress;

    // When geocode is true, we geocode the address
    if (geocode) {
      updateLocationViaGeocoding(newAddressComponents);
    }

    setAddressComponents(newAddressComponents);

    // Set the street and number for non-split address details
    if (!enableDeliverySplitAddressDetails) {
      setStreetAndNumber(newAddressComponents.addressLine1 || newAddressComponents.route || '');
    }
  };

  const onEditAddress = (address: IDeliveryAddress) => {
    handleStepChange(StoreLocatorSteps.EDIT_ADDRESS);

    if (typeof setEditedAddress === 'function') {
      setEditedAddress(editedAddress || address);
    }
  };
  // #endregion

  // #region Place change handlers
  // Handle place data change
  // This function is used to update the address components and the coordinates from the place data
  // Calling this function will take the user to the confirm address step
  // It's called when the user:
  // - selects an address from the delivery address form input
  // - selects an address from the saved addresses
  // - clicks the current location button
  const handlePlaceChange = (placeId: string, placeData: IPlaceData) => {
    // Reset delivery instructions if the address has changed
    if (!isEqual(placeData.address, addressComponents)) {
      setDeliveryInstructions('');
    }

    // Validate address
    if (!enableDeliveryGeolocation && (!placeData.address || !placeData.address.addressLine1)) {
      handleInvalidAddress();
    }

    setCoordinates(placeData.coordinates);
    updateAddressComponents(placeData.address);

    // Set the street name for the street autocomplete input when enableReduceDeliveryError is enabled
    if (enableReduceDeliveryError) {
      const googleRoute = placeData.address.route ?? '';
      const tomtomAddress = placeData.address.addressLine1 ?? '';

      const finalRoute = googleRoute || (enableMapFallBack ? tomtomAddress : '');

      setAutocompleteStreetName(finalRoute);
      setAutocompletePreviousValue(finalRoute);
    }

    // Update the manual coordinates when enablePersistAddressOnStoreSelector is enabled
    if (enablePersistAddressOnStoreSelector) {
      updateManualCoordinates(
        buildAddressFromObject(placeData.address),
        placeId,
        placeData.coordinates
      );
    }

    // Navigate to the confirm address step
    handleStepChange(StoreLocatorSteps.CONFIRM_ADDRESS);
  };

  // Handle place selected in delivery address form input
  const handlePlaceIdSelected = async (placeId: string) => {
    if (!placeId || isSubmitting) {
      return;
    }

    // Geocode the place id to get the place data
    const placeData = await getDetailsPlaceData(placeId);

    // Update the address components and the coordinates
    handlePlaceChange(placeId, placeData);

    if (enableReduceDeliveryError) {
      setDetailsLoading(true);
    }

    // This timeout was added on purpose so that the user can see a loading component
    // in the address text, because the Google API is very fast and it is difficult
    // to identify when the address has changed. This was requested by the UX team.
    setTimeout(() => {
      setDetailsLoading(false);
    }, 2000);
  };

  // Handle address selected in delivery address form input
  const handleAddressSelected = async (newAddressString: string, placeId: string) => {
    setAddressString(newAddressString);
    setDeliveryAddressFormInputMessage('');

    if (!placeId) {
      handleInvalidAddress();
      updateAddressComponents(null);
      return;
    }

    setValidation({
      ...validation,
      address: FormValidationState.PRISTINE,
    });

    await handlePlaceIdSelected(placeId);
  };

  const handleConfirmManualAddress = useCallback(() => {
    if (manualPlaceId) {
      handlePlaceIdSelected(manualPlaceId);
    }
  }, [manualPlaceId]);
  // #endregion

  // #region Geolocation handlers
  // Handle use current location click in delivery address footer and delivery addresses
  const handleUseCurrentLocationClick = async () => {
    if (userCoordinates) {
      // Geocode the user coordinates
      const geocodeResult = await getCoordinateDetails(userCoordinates);

      if (geocodeResult) {
        const placeData = parseGeocodeResult(geocodeResult);

        handlePlaceChange(geocodeResult.place_id, placeData);
      }
    }
  };

  // Handle geolocation permissions
  const handleGeolocationPrompt = useCallback(() => {
    if (isPermissionDenied) {
      setGeolocationDeniedModalOpen(true);
      return;
    } else if (isPermissionPrompt) {
      setPermissionChecking();
      loadCurrentPosition();
    }
  }, [isPermissionDenied, isPermissionPrompt, loadCurrentPosition, setPermissionChecking]);

  // Handle share location click in delivery address footer and delivery addresses
  const handleShareLocationClick = async () => {
    handleGeolocationPrompt();
  };
  // #endregion

  // #region Confirm address step handlers
  const handleAddressChange = useCallback((str: string) => {
    setStreetAndNumber(str);
    setAddressString(str);
  }, []);

  const handleStreetNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const addressLine1Payload = {
        streetNumber: addressComponents?.streetNumber,
        route: e.target.value,
      };
      updateAddressComponents({
        addressLine1: formatAddressLine1(
          addressLine1Payload,
          addressFormat === AddressFormatType.NameNumber
        ),
        route: e.target.value,
      });
    },
    [addressComponents, addressFormat]
  );

  const handleStreetNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setStreetNumberError('');

      const addressLine1Payload = {
        streetNumber: e.target.value,
        route: addressComponents?.route,
      };

      updateAddressComponents(
        {
          addressLine1: formatAddressLine1(
            addressLine1Payload,
            addressFormat === AddressFormatType.NameNumber
          ),
          streetNumber: e.target.value,
        },
        true
      );
    },
    [addressComponents, addressFormat, updateLocationViaGeocoding]
  );

  const handleAutocompleteStreetNameChange = (value: string) => {
    setAutocompleteStreetName(value);
    setAutocompleteStreetNameError('');
  };

  const handleAddressLine2Change = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateAddressComponents({ addressLine2: `${e.target.value}` });
    },
    [addressComponents]
  );

  const handleDeliveryInstructionsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDeliveryInstructions(`${e.target.value}`);
  }, []);

  const handleAddressNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateAddressComponents({ alias: `${e.target.value}` });
    },
    [addressComponents]
  );

  const resetOnAddressConfirmationReturn = useCallback(() => {
    setCoordinates(undefined);
    handleStepChange(StoreLocatorSteps.ADDRESS_SELECTOR);

    // Clear form validations
    setAutocompleteStreetNameError('');
    setStreetNumberError('');

    if (enableDeliveryGeolocation && enablePersistAddressOnStoreSelector) {
      setAddressString(manualAddress);
      return;
    }

    setAddressString('');
  }, [enableDeliveryGeolocation, enablePersistAddressOnStoreSelector, manualAddress]);

  const onClickChangeLink = () => {
    resetOnAddressConfirmationReturn();

    trackEvent({
      name: CustomEventNames.CLICK_EVENT,
      type: EventTypes.Other,
      attributes: {
        component: ClickEventComponentNames.LINK,
        headerText: formatMessage({ id: 'change' }),
      },
    });
  };
  // #endregion

  // #region Modal handlers
  const onCancelDistanceWarning = () => {
    setCoordinates(undefined);
    setPreviousStep(null);
    updateAddressComponents(null);
    setAddressString('');
    setIsSubmitting(false);
    handleStepChange(StoreLocatorSteps.ADDRESS_SELECTOR);
    dismissDialog();
  };

  const onConfirmDistanceWarning = () => {
    confirmDelivery();
    dismissDialog();
  };

  const onConfirmUnrecognizedNumber = () => {
    trackEvent({
      name: CustomEventNames.BUTTON_CLICK,
      type: EventTypes.Other,
      attributes: {
        Name: 'Yes, Deliver Here',
        Path: location.pathname,
      },
    });

    setShowAddressUnrecognizedModal(false);
    validateCustomerFaraway();
  };

  const onUnrecognizedNumberBack = () => {
    trackEvent({
      name: CustomEventNames.BUTTON_CLICK,
      type: EventTypes.Other,
      attributes: {
        Name: 'No, Change Address',
        Path: location.pathname,
      },
    });

    setShowAddressUnrecognizedModal(false);
    setIsSubmitting(false);
  };
  // #endregion

  // #region Effects
  useEffect(() => {
    if (enableSaveDeliveryAddress && deliveryAddress?.address && !skipSavedAddressClosedStore) {
      setAddressString('');
      setDeliveryAddressFormInputMessage('');
      setValidation({ ...validation, address: FormValidationState.PRISTINE });
      setCoordinates(deliveryAddress.coordinates);
      updateAddressComponents(deliveryAddress.address);
      setDeliveryInstructions('');

      if (enableReduceDeliveryError) {
        setAutocompleteStreetName(deliveryAddress.address?.route ?? '');
      }
    }
  }, [deliveryAddress, enableSaveDeliveryAddress]); // eslint-disable-line react-hooks/exhaustive-deps
  // #endregion

  return (
    <>
      <ModalContent $deliveryOnSameScreen={enableDeliveryOnSameScreen}>
        {!enableDeliveryOnSameScreen && <DeliveryAddressFormHeader />}
        <StyledForm onSubmit={handleSubmit}>
          {matchCurrentStep(StoreLocatorSteps.ADDRESS_SELECTOR) && (
            <>
              <DeliveryAddressFormInput
                deliveryAddressInputRef={deliveryAddressInputRef}
                message={deliveryAddressFormInputMessage}
                onAddressSelected={handleAddressSelected}
                onValueChange={setAddressString}
                setMessage={setDeliveryAddressFormInputMessage}
                value={addressString}
              />
              {showConfirmManualAddressButton && (
                <RightArrow
                  role="button"
                  aria-label={formatMessage({ id: 'deliveryConfirmDetails' })}
                  onClick={handleConfirmManualAddress}
                />
              )}
            </>
          )}
          {Boolean(deliveryAddressFormInputMessage) && (
            <VisuallyHidden role="alert">
              {`${formatMessage({ id: 'thereIsAnErrorInThisForm' })}.  `}
            </VisuallyHidden>
          )}
          {matchCurrentStep(StoreLocatorSteps.CONFIRM_ADDRESS) && shouldShowMap && (
            <MapDeliveryAddress
              coordinates={coordinates}
              enableDeliveryGeolocation={enableDeliveryGeolocation}
            />
          )}
          {!enableDeliveryGeolocation && (
            <AdditionalInformation>
              {formatMessage({ id: 'additionalAddressInformation' })}
            </AdditionalInformation>
          )}

          {matchCurrentStep(StoreLocatorSteps.CONFIRM_ADDRESS) &&
            enableDeliveryGeolocation &&
            coordinates && (
              <>
                <AddressDetailsSection>
                  <AddressDetailsBorder>
                    <LocationDetails
                      mainText={addressTitle}
                      secondaryText={getAddressSubtitle(addressComponents)}
                      isLoading={detailsLoading}
                    />
                    <ChangeButton onClick={onClickChangeLink}>
                      {formatMessage({ id: 'change' })}
                    </ChangeButton>
                  </AddressDetailsBorder>
                </AddressDetailsSection>

                {enableDeliverySplitAddressDetails ? (
                  <AddressWrapper>
                    <Formik
                      initialValues={{
                        streetName: addressComponents?.route,
                        streetNumber: addressComponents?.streetNumber,
                      }}
                      onSubmit={() => {
                        // maybe refactor this and have everything handled by formik instead?
                      }}
                    >
                      {() => (
                        <>
                          {enableReduceDeliveryError ? (
                            <AutocompleteAddressInput
                              autoFocus
                              autoComplete="new-password" // TODO: https://rbictg.atlassian.net/browse/IBFEC-3369
                              data-private="redact"
                              data-dd-privacy="mask"
                              data-testid="delivery-street-name-input"
                              label={`${formatMessage({ id: 'deliveryAddressStreetName' })}*`}
                              matchTypes={['address']}
                              onAddressSelected={(_, id: string) => handlePlaceIdSelected(id)}
                              onBlur={() => setAutocompleteStreetName(autocompletePreviousValue)}
                              onInputValueChange={handleAutocompleteStreetNameChange}
                              required
                              shouldExpandPostalCodeToAddresses
                              value={autocompleteStreetName}
                              message={autocompleteStreetNameError}
                              eventSource={CustomEventNames.STREET_NAME}
                              isDescriptionWithMainText
                            />
                          ) : (
                            <Field
                              name="streetName"
                              component={FormikTextInput}
                              required
                              label={formatMessage({ id: 'deliveryAddressStreetName' })}
                              data-testid="delivery-street-name-input"
                              value={addressComponents?.route}
                              onChange={handleStreetNameChange}
                            />
                          )}
                          <FieldWrapper>
                            {enableReduceDeliveryError ? (
                              <TextInput
                                name="streetNumber"
                                label={`${formatMessage({ id: 'deliveryAddressStreetNumber' })}*`}
                                data-testid="delivery-street-number-input"
                                value={addressComponents?.streetNumber ?? ''}
                                onChange={handleStreetNumberChange}
                                errorMessage={streetNumberError}
                              />
                            ) : (
                              <Field
                                name="streetNumber"
                                component={FormikTextInput}
                                required
                                label={formatMessage({ id: 'deliveryAddressStreetNumber' })}
                                data-testid="delivery-street-number-input"
                                value={addressComponents?.streetNumber}
                                onChange={handleStreetNumberChange}
                              />
                            )}

                            {enableRequireAddress2 ? (
                              <Field
                                name="instructions"
                                component={FormikTextInput}
                                label={formatMessage({ id: 'deliveryAddressLine2' })}
                                data-testid="delivery-instructions-input"
                                value={deliveryInstructions}
                                onChange={handleDeliveryInstructionsChange}
                              />
                            ) : (
                              <Field
                                name="addressLine2"
                                component={FormikTextInput}
                                label={formatMessage({ id: 'deliveryAddressLine2' })}
                                data-testid="delivery-address-line2-input"
                                value={addressComponents?.addressLine2}
                                onChange={handleAddressLine2Change}
                              />
                            )}
                          </FieldWrapper>

                          {enableSavedAddressesImprovements && (
                            <>
                              <AddressNameLabel>
                                {formatMessage({ id: 'saveThisAddressLabel' })}
                              </AddressNameLabel>
                              <Field
                                name="name"
                                component={FormikTextInput}
                                value={addressComponents?.alias}
                                onChange={handleAddressNameChange}
                                data-testid="delivery-address-name-input"
                                label={formatMessage({ id: 'saveThisAddressPlaceholder' })}
                              />
                            </>
                          )}
                        </>
                      )}
                    </Formik>
                  </AddressWrapper>
                ) : (
                  <>
                    <Box>
                      <DeliveryAddressFormInput
                        deliveryAddressInputRef={deliveryAddressInputRef}
                        labelKeyId="deliveryAddressStreetNumber"
                        message={deliveryAddressFormInputMessage}
                        onAddressSelected={handleAddressSelected}
                        onValueChange={handleAddressChange}
                        setMessage={setDeliveryAddressFormInputMessage}
                        value={
                          enableDeliveryShowOnlyStreetNameAndNumber
                            ? streetAndNumber
                            : addressString
                        }
                      />
                    </Box>
                    <HelperText>{formatMessage({ id: 'deliveryAddressReviewText' })}</HelperText>
                  </>
                )}
              </>
            )}
          {(showDeliveryAddresses || showRecentAddresses) && !coordinates ? (
            <DeliveryAddresses
              addresses={deliveryAddresses ?? []}
              recentAddresses={recentAddresses}
              setAddress={setAddress}
              showDeliveryAddresses={showDeliveryAddresses && !coordinates}
              showRecentAddresses={showRecentAddresses}
              setCurrentStep={setCurrentStep}
              currentStep={currentStep}
              isLoading={isLoading}
              onShareLocationClick={handleShareLocationClick}
              onUseCurrentLocationClick={handleUseCurrentLocationClick}
              onEditAddress={onEditAddress}
            />
          ) : (
            showFooter && (
              <DeliveryAddressFormFooter
                coordinates={coordinates}
                isDisabled={isDeliveryHereButtonDisabled()}
                isLoading={isLoading}
                onShareLocationClick={handleShareLocationClick}
                onUseCurrentLocationClick={handleUseCurrentLocationClick}
                matchCurrentStep={matchCurrentStep}
              />
            )
          )}
        </StyledForm>
        {deliveryLogo?.locale && (
          <DeliveryLogoContainer>
            <Picture image={deliveryLogo?.locale} alt={formatMessage({ id: 'deliveryLogoAlt' })} />
          </DeliveryLogoContainer>
        )}
      </ModalContent>
      {addressComponents && (
        <ConfirmLocationModal
          streetName={addressComponents.addressLine1}
          city={`${addressComponents.city}, ${convertCountryStringToIsoCountryCode2(
            addressComponents.country || ''
          )}`}
          isDelivery
          isGeolocationEnabled={isGeolocationEnabled}
          onConfirm={onConfirmDistanceWarning}
          onDismiss={onCancelDistanceWarning}
        />
      )}
      {showAddressUnrecognizedModal && (
        <UnrecognizedStreetNumberModal
          onConfirm={onConfirmUnrecognizedNumber}
          onDismiss={onUnrecognizedNumberBack}
          streetNumber={addressComponents?.streetNumber ?? ''}
        />
      )}
      <GeolocationDeniedModal
        isOpen={geolocationDeniedModalOpen}
        dismissModal={() => setGeolocationDeniedModalOpen(false)}
      />
    </>
  );
};
